import { useState } from "react";
import { ChevronDown, ChevronUp, Check, Minus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface ExcludableItem {
  id: string;
  name: string;
  subtitle?: string;
  badge?: string;
  amount?: number;
  confidence?: number;
}

interface ItemExclusionListProps {
  items: ExcludableItem[];
  excludedIds: string[];
  onToggleItem: (itemId: string, excluded: boolean) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  title?: string;
  maxVisible?: number;
  disabled?: boolean;
  formatAmount?: (amount: number) => string;
  className?: string;
}

/**
 * List of items with checkboxes for excluding individual items from import
 *
 * Used in the unified onboarding flow to let users exclude specific
 * vendors, customers, or bank accounts from being created
 */
export function ItemExclusionList({
  items,
  excludedIds,
  onToggleItem,
  onSelectAll,
  onDeselectAll,
  title,
  maxVisible = 10,
  disabled = false,
  formatAmount,
  className,
}: ItemExclusionListProps) {
  const [showAll, setShowAll] = useState(false);

  const excludedSet = new Set(excludedIds);
  const selectedCount = items.length - excludedSet.size;
  const allSelected = excludedSet.size === 0;
  const noneSelected = excludedSet.size === items.length;
  const someSelected = !allSelected && !noneSelected;

  const visibleItems = showAll ? items : items.slice(0, maxVisible);
  const hasMore = items.length > maxVisible;

  const defaultFormatAmount = (amount: number) =>
    new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount / 100);

  const formatter = formatAmount || defaultFormatAmount;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Header with bulk actions */}
      <div className="flex items-center justify-between">
        {title && (
          <span className="text-sm font-medium text-muted-foreground">
            {title} ({selectedCount}/{items.length} selected)
          </span>
        )}
        {(onSelectAll || onDeselectAll) && items.length > 1 && (
          <div className="flex items-center gap-2">
            {onSelectAll && !allSelected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onSelectAll}
                disabled={disabled}
                className="h-7 text-xs"
              >
                <Check className="h-3 w-3 mr-1" />
                Select all
              </Button>
            )}
            {onDeselectAll && !noneSelected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDeselectAll}
                disabled={disabled}
                className="h-7 text-xs"
              >
                <Minus className="h-3 w-3 mr-1" />
                Deselect all
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Item list */}
      <div className="space-y-1 border rounded-lg divide-y">
        {visibleItems.map(item => {
          const isExcluded = excludedSet.has(item.id);
          const isSelected = !isExcluded;

          return (
            <label
              key={item.id}
              className={cn(
                "flex items-center gap-3 p-3 cursor-pointer transition-colors hover:bg-muted/50",
                isExcluded && "opacity-60",
                disabled && "cursor-not-allowed"
              )}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={checked => {
                  onToggleItem(item.id, !checked);
                }}
                disabled={disabled}
                aria-label={`Include ${item.name}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "font-medium truncate",
                      isExcluded && "text-muted-foreground line-through"
                    )}
                  >
                    {item.name}
                  </span>
                  {item.badge && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {item.badge}
                    </Badge>
                  )}
                </div>
                {item.subtitle && (
                  <p className="text-xs text-muted-foreground truncate">
                    {item.subtitle}
                  </p>
                )}
              </div>
              {item.amount !== undefined && (
                <span
                  className={cn(
                    "text-sm font-medium tabular-nums shrink-0",
                    item.amount >= 0
                      ? "text-[var(--success)]"
                      : "text-destructive"
                  )}
                >
                  {formatter(item.amount)}
                </span>
              )}
            </label>
          );
        })}
      </div>

      {/* Show more/less */}
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAll(!showAll)}
          className="w-full"
        >
          {showAll ? (
            <>
              <ChevronUp className="h-4 w-4 mr-2" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-2" />
              Show {items.length - maxVisible} more
            </>
          )}
        </Button>
      )}
    </div>
  );
}
